import type { TableRowType } from 'components/editor/grid/reduxStore/table.types';
import { ColumnTypeIdentifier } from 'components/editor/grid/reduxStore/table.types';
import type { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import type { GridRowModel } from '@mui/x-data-grid-pro';
import { CustomTableCell, CustomTableCellInPreview } from '../components/CustomTableCell';
import { isTaxOrDiscountRow } from 'muiTheme/dataGridUtils';
import { formatPriceWithDollarSign } from '../helper';

interface TableNumericCellProps {
  params: GridCellParams;
  columnType: ColumnTypeIdentifier;
  numberOfDecimalPlaces?: number;
  isDocumentLocked?: boolean;
  documentCurrencySymbol?: string;
  valueSuffix?: string;
}

export const PRICING_COLUMNS = [ColumnTypeIdentifier.PRICE, ColumnTypeIdentifier.SUBTOTAL];

export const TableNumericCell = ({
  params,
  columnType,
  numberOfDecimalPlaces,
  isDocumentLocked,
  documentCurrencySymbol,
  valueSuffix,
}: TableNumericCellProps) => {
  const TableCellComponent = isDocumentLocked ? CustomTableCellInPreview : CustomTableCell;

  if (!params) return <></>;

  if (params.value === undefined || params.value === null) {
    return <TableCellComponent params={params} value="" />;
  }

  const isPricingColumn = PRICING_COLUMNS.includes(columnType);
  const parsedValue = parseFloat(params.value.toString());
  // Pricing columns (PRICE/SUBTOTAL) must keep an explicit 0 visible instead of
  // treating it as "empty" via JS truthiness. Non-pricing columns (e.g.
  // MULTIPLIER) keep the original truthy check so a 0 quantity stays blank.
  const isDisplayableValue = isPricingColumn ? Number.isFinite(parsedValue) : Boolean(parsedValue);
  let displayedValue = isDisplayableValue ? params.value.toString() : '';
  const row = params.row as TableRowType;
  const shouldNotAppendCurrencySign = !isPricingColumn || displayedValue === '' || isTaxOrDiscountRow(row);

  if (shouldNotAppendCurrencySign) {
    return <TableCellComponent params={params} value={displayedValue} />;
  }
  displayedValue = formatPriceWithDollarSign(displayedValue, numberOfDecimalPlaces, documentCurrencySymbol);
  if (valueSuffix) {
    displayedValue = `${displayedValue}${valueSuffix}`;
  }

  return <TableCellComponent params={params} value={displayedValue} numberOfDecimalPlaces={numberOfDecimalPlaces} />;
};

export const getIsCellEditingAllowed = ({ row, colDef }: { row: GridRowModel; colDef: GridColDef }): boolean => {
  return row?.cellConfig?.[colDef.field]?.isEditingActive ?? false;
};
